import { useState, useMemo } from "react";
import BlogHeader from "../components/BlogHeader";
import BlogCard from "../components/BlogCard";
import BlogCTA from "../components/BlogCTA";
import SEO from "../components/SEO";
import { blogPosts, blogCategories, getFeaturedPosts } from "../data/blogPosts";
import { Badge } from "../components/ui/badge";
import { Sparkles } from "lucide-react";
import SocialLinks from "../components/SocialLinks";

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const featured = getFeaturedPosts();

  const sortedPosts = useMemo(() => {
    return [...blogPosts].sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
  }, []);

  const filteredPosts = useMemo(() => {
    if (!selectedCategory) return sortedPosts;
    return sortedPosts.filter((p) => p.category === selectedCategory);
  }, [selectedCategory, sortedPosts]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Ataric Blog - Aprenda Inglês com IA",
    description: "Dicas, estratégias e guias completos para aprender inglês com inteligência artificial. Destrave sua fluência com o Ataric.",
    url: "https://www.ataric.com.br/blog",
    publisher: {
      "@type": "Organization",
      name: "Ataric",
      url: "https://www.ataric.com.br",
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Como aprender inglês com inteligência artificial?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Com o Ataric, você pratica conversação em inglês com IA avançada, recebendo correções em tempo real de pronúncia, gramática e vocabulário, tudo personalizado ao seu nível."
        }
      },
      {
        "@type": "Question",
        name: "A IA pode substituir um professor de inglês?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A IA é um complemento poderoso. Com disponibilidade 24/7, personalização e paciência infinita, a IA do Ataric ajuda você a praticar muito mais do que seria possível apenas com aulas tradicionais."
        }
      },
      {
        "@type": "Question",
        name: "Quanto tempo leva para ficar fluente em inglês com IA?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Com prática consistente de 20-30 minutos diários usando o Ataric, a maioria dos usuários percebe melhoras significativas em 3-6 meses, dependendo do nível inicial."
        }
      }
    ]
  };

  return (
    <div className="blog-scope">
      <SEO
        title="Blog Ataric - Aprenda Inglês com IA | Dicas e Guias"
        description="Dicas, estratégias e guias completos para aprender inglês com inteligência artificial. Destrave sua fluência com o Ataric."
        canonical="/blog"
        jsonLd={jsonLd}
      />
      <BlogHeader />

      <main className="container mx-auto max-w-5xl px-4 py-12">
        <section className="mb-14 text-center relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-6 tracking-wide">
              <Sparkles className="h-3.5 w-3.5" /> BLOG ATARIC
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold text-foreground mb-5 leading-tight">
              Aprenda inglês com{" "}
              <span className="gradient-text glow-text">Inteligência Artificial</span>
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Dicas, guias e estratégias para destravar sua fluência em inglês — direto de quem entende de IA.
            </p>
          </div>
        </section>

        <div className="flex flex-wrap gap-2 mb-12 justify-center">
          <Badge
            variant={selectedCategory === null ? "default" : "secondary"}
            className={`cursor-pointer transition-all duration-300 px-4 py-1.5 text-sm rounded-full ${
              selectedCategory === null ? "shadow-[0_0_15px_-3px_hsl(270_70%_55%/0.4)]" : "hover:bg-secondary/80"
            }`}
            onClick={() => setSelectedCategory(null)}
          >
            Todos
          </Badge>
          {blogCategories.map((cat) => (
            <Badge
              key={cat}
              variant={selectedCategory === cat ? "default" : "secondary"}
              className={`cursor-pointer transition-all duration-300 px-4 py-1.5 text-sm rounded-full ${
                selectedCategory === cat ? "shadow-[0_0_15px_-3px_hsl(270_70%_55%/0.4)]" : "hover:bg-secondary/80"
              }`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </Badge>
          ))}
        </div>

        {!selectedCategory && (
          <section className="mb-14">
            <h2 className="font-display text-xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" /> Destaques
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {featured.slice(0, 2).map((post) => (
                <BlogCard key={post.id} post={post} featured />
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="font-display text-xl font-bold text-foreground mb-6">
            {selectedCategory || "Todos os Artigos"}
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </section>

        <BlogCTA />

        <section className="mt-16 mb-12">
          <h2 className="font-display text-2xl font-bold text-foreground mb-8 text-center">
            Perguntas Frequentes
          </h2>
          <div className="space-y-4 max-w-3xl mx-auto">
            {faqJsonLd.mainEntity.map((item, i) => (
              <details key={i} className="group rounded-xl border border-border/50 bg-card overflow-hidden glow-card">
                <summary className="cursor-pointer px-6 py-4 font-display font-semibold text-foreground flex items-center justify-between text-sm md:text-base">
                  {item.name}
                  <span className="text-primary transition-transform duration-300 group-open:rotate-45 text-lg">+</span>
                </summary>
                <div className="px-6 pb-4 text-muted-foreground text-sm leading-relaxed border-t border-border/30 pt-3">
                  {item.acceptedAnswer.text}
                </div>
              </details>
            ))}
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

        <footer className="border-t border-border/50 pt-8 mt-12 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Ataric — Aprenda a Falar Inglês com IA</p>
          <p className="mt-1">Destrave sua fluência.</p>
          <SocialLinks />
        </footer>
      </main>
    </div>
  );
};

export default Blog;
